// /opt/ptg01-v1/front-extranet/components/TopBar.tsx
// TopBar consultant — bandeau au-dessus col 2+3 (pattern pageHeader admin).
// Titre + sous-titre derives du pathname, ou fournis en props pour personnalisation.
// User menu a droite. Left: 260px (align sidebar). Hauteur 56px.
'use client';

import React, { useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { LogOut, ChevronDown, Plus, Home, Search } from 'lucide-react';
import { openCommandPalette } from './CommandPaletteHost';
import { fetchExtranet } from '@/lib/fetchExtranet';
import { useAuthOptional } from '@/lib/AuthContext';
import { useTopBarOverride } from '@/lib/TopBarOverrideContext';
import ExpenseQuickCreateSheet from './ExpenseQuickCreateSheet';
import { QuickActionsSheet } from './QuickActionsSheet';

// Map route → { eyebrow, title }. Fallback : le dernier segment capitalise.
const ROUTE_LABELS: Record<string, { eyebrow: string; title: string }> = {
    '/':                              { eyebrow: '',                     title: 'Tableau de bord' },
    // Activité — Missions et CRA aplatis en niveau 0 dans la sidebar
    '/cras':                          { eyebrow: 'CRA',                  title: 'Mes CRA' },
    '/cras/declarer':                 { eyebrow: 'CRA',                  title: 'Valider mon CRA' },
    '/missions':                      { eyebrow: 'Missions',             title: 'Mes missions' },
    // Compte d'activité — niveau 0 sous Tableau de bord
    '/compte-activite':               { eyebrow: '',                     title: 'Compte d\'activité' },
    // Salaires & versements — niveau 0 (parent "Revenus & paie" supprimé)
    '/finance':                       { eyebrow: 'Salaires & versements', title: 'Mouvements bancaires' },
    '/finance/salaires':              { eyebrow: 'Salaires & versements', title: 'Salaires & versements' },
    '/finance/salaires/declarer':     { eyebrow: 'Salaires & versements', title: 'Déclarer mon salaire' },
    '/finance/reserves':              { eyebrow: 'Salaires & versements', title: 'Mes réserves' },
    // Dépenses
    '/depenses':                      { eyebrow: 'Notes de frais', title: 'Vue d\'ensemble' },
    '/depenses/ponctuelles':          { eyebrow: 'Notes de frais', title: 'Notes ponctuelles' },
    '/depenses/recurrentes':          { eyebrow: 'Notes de frais', title: 'Notes récurrentes' },
    '/depenses/refacturees':          { eyebrow: 'Notes de frais', title: 'Notes refacturables' },
    '/depenses/creer':                { eyebrow: 'Notes de frais', title: 'Nouvelle note de frais' },
    '/depenses/modifier':             { eyebrow: 'Notes de frais', title: 'Modifier une note de frais' },
    '/depenses/abonnements':          { eyebrow: 'Notes de frais', title: 'Configuration récurrences' },
    '/depenses/abonnements/creer':    { eyebrow: 'Notes de frais', title: 'Nouvel abonnement' },
    '/depenses/abonnements/adresses': { eyebrow: 'Notes de frais', title: 'Mes adresses favorites' },
    // Documents / Avantages
    '/documents':                     { eyebrow: 'Documents',            title: 'Documents' },
    '/avantages':                     { eyebrow: 'Avantages',            title: 'Mes avantages' },
    // Bas de sidebar
    '/notifications':                 { eyebrow: 'Espace consultant',    title: 'Messages' },
    '/aide':                          { eyebrow: 'Espace consultant',    title: 'Aide & support' },
    // Mon profil (EXT-L0 : les sous-adresses /mon-compte/<onglet> et /parametres ne sont plus que
    // des redirections vers /mon-compte?onglet=…)
    '/mon-compte':                    { eyebrow: '',                     title: 'Mon profil' },
};

function resolveLabels(pathname: string): { eyebrow: string; title: string } {
    if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
    // Fallback : chercher un prefix qui matche
    const match = Object.keys(ROUTE_LABELS)
        .filter(k => k !== '/' && pathname.startsWith(k))
        .sort((a, b) => b.length - a.length)[0];
    if (match) return ROUTE_LABELS[match];
    return { eyebrow: 'Espace consultant', title: 'Nexvia' };
}

export default function TopBar() {
    const router = useRouter();
    const pathname = usePathname() || '/';
    const auth = useAuthOptional();
    const user = auth?.user ?? null;
    const [menuOpen, setMenuOpen] = useState(false);
    const [quickCreateOpen, setQuickCreateOpen] = useState(false);
    const [quickActionsOpen, setQuickActionsOpen] = useState(false);

    // Bouton "+ note de frais" : visible uniquement sur les routes /depenses/* (mais pas sur le formulaire de saisie).
    // Action spécifique : créer une nouvelle note de frais.
    const showQuickCreate = pathname.startsWith('/depenses') && !pathname.startsWith('/depenses/creer');
    // Bouton "+ actions rapides" : aiguillage générique (CRA / missions / compte d'activité / frais / messages / aide).
    // Visible sur l'accueil, les CRA, le compte d'activité, les notifications.
    const showQuickActions =
        pathname === '/' ||
        pathname.startsWith('/cras') ||
        pathname.startsWith('/compte-activite') ||
        pathname.startsWith('/notifications');

    const handleLogout = async () => {
        try { await fetchExtranet('/api/auth/logout', { method: 'POST' }); } catch { /* noop */ }
        auth?.setUser(null);
        router.push('/auth/login');
    };

    const { override } = useTopBarOverride();
    const defaults = resolveLabels(pathname);
    const eyebrow = override?.eyebrow ?? defaults.eyebrow;
    const title = override?.title ?? defaults.title;
    const subtitle = override?.subtitle;
    const badge = override?.badge;
    const items = override?.items;
    const displayName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Consultant' : 'Consultant';
    const initials = user ? `${(user.firstName || '?')[0]}${(user.lastName || '?')[0]}`.toUpperCase() : '??';

    return (
        <>
        <div
            className="fixed top-0 left-0 lg:left-[260px] right-0 h-20 bg-white border-b border-slate-200 z-30 flex items-center justify-between px-4 lg:px-6 font-sans"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
            <div className="flex items-center gap-2.5 min-w-0">
                {/* Mini logo mobile (la sidebar n'est pas visible < lg) */}
                <div className="lg:hidden w-8 h-8 rounded-lg bg-[#000091] text-white flex items-center justify-center text-[13px] font-black shadow-sm shadow-[#000091]/20 shrink-0">
                    N
                </div>
                <div className="flex flex-col min-w-0">
                    {eyebrow && (
                        <span className="text-[10px] font-semibold text-[#000091] uppercase tracking-wider truncate">{eyebrow}</span>
                    )}
                    {items && items.length > 0 ? (
                        // Mode multi-items : empile chaque entrée verticalement (ex: 2 missions actives)
                        <div className="flex flex-col min-w-0 leading-tight">
                            {items.map((it, i) => (
                                <div key={i} className="flex items-center gap-1.5 min-w-0">
                                    <span className="text-[12px] lg:text-[13px] font-bold text-slate-900 truncate">{it.title}</span>
                                    {it.badge && (
                                        <span className={`inline-flex items-center text-[8px] font-bold uppercase px-1 py-px rounded shrink-0 ${it.badge.bgClass} ${it.badge.textClass}`}>
                                            {it.badge.label}
                                        </span>
                                    )}
                                    {it.subtitle && (
                                        <span className="text-[10px] text-slate-500 truncate">· {it.subtitle}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-1.5 min-w-0">
                                {/* Si pas d'eyebrow (mode 1 ligne), le titre porte la couleur Nexvia. */}
                                <span className={`text-[14px] lg:text-[15px] font-bold leading-tight truncate ${eyebrow ? 'text-slate-900' : 'text-[#000091]'}`}>{title}</span>
                                {badge && (
                                    <span className={`inline-flex items-center text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${badge.bgClass} ${badge.textClass}`}>
                                        {badge.label}
                                    </span>
                                )}
                            </div>
                            {subtitle && (
                                <span className="text-[10px] text-slate-500 leading-tight truncate">{subtitle}</span>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
                {/* Recherche globale ⌘K — visible partout. Icône seule en mobile, libellé + raccourci en desktop. */}
                <button
                    onClick={openCommandPalette}
                    title="Rechercher (⌘K)"
                    aria-label="Rechercher"
                    data-testid="topbar-search"
                    className="inline-flex items-center gap-2 h-9 px-2.5 sm:px-3 rounded-lg bg-white border-2 border-slate-200 text-slate-500 text-[12px] hover:border-[#000091]/40 hover:text-[#000091] active:scale-95 transition-all"
                >
                    <Search className="w-4 h-4" />
                    <span className="hidden sm:inline">Rechercher</span>
                    <kbd className="hidden lg:inline-flex items-center justify-center h-[18px] px-1.5 rounded bg-slate-100 border border-slate-300 text-[10px] font-bold text-slate-600 ml-1">⌘K</kbd>
                </button>

                {/* Bouton Accueil — visible partout sauf sur la page d'accueil elle-même */}
                {pathname !== '/' && (
                    <button
                        onClick={() => router.push('/')}
                        title="Retour au tableau de bord"
                        aria-label="Retour au tableau de bord"
                        className="inline-flex items-center gap-1.5 h-9 px-2.5 sm:px-3 rounded-lg bg-white border-2 border-[#000091]/25 text-[#000091] font-semibold text-[12px] hover:bg-[#000091]/[0.04] hover:border-[#000091]/40 active:scale-95 transition-all"
                    >
                        <Home className="w-4 h-4" />
                        <span className="hidden sm:inline">Accueil</span>
                    </button>
                )}

                {/* Action contextuelle "Créer une note de frais" — visible /depenses/* uniquement */}
                {showQuickCreate && (
                    <button
                        onClick={() => setQuickCreateOpen(true)}
                        title="Créer une note de frais"
                        aria-label="Créer une note de frais"
                        className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-4 rounded-lg bg-[#000091] text-white font-semibold text-[13px] hover:bg-[#1e40af] active:scale-95 transition-all shadow-sm shadow-[#000091]/20"
                    >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline">Note de frais</span>
                    </button>
                )}

                {/* Action contextuelle "Actions rapides" — sheet d'aiguillage générique
                    visible sur Accueil, CRA, Compte d'activité, Messages (notifications). Refonte : lot L1. */}
                {showQuickActions && (
                    <button
                        onClick={() => setQuickActionsOpen(true)}
                        title="Actions rapides"
                        aria-label="Actions rapides"
                        className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-4 rounded-lg bg-[#000091] text-white font-semibold text-[13px] hover:bg-[#1e40af] active:scale-95 transition-all shadow-sm shadow-[#000091]/20"
                    >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline">Actions rapides</span>
                    </button>
                )}

                {/* Cloche notifications retirée — l'accès est déjà disponible via « Messages » dans la nav (bottom tab bar mobile + sidebar desktop). */}

                <div className="h-6 w-px bg-slate-200 mx-1" />

                <div className="relative">
                    <button
                        onClick={() => setMenuOpen(v => !v)}
                        data-testid="topbar-user-menu"
                        aria-expanded={menuOpen}
                        aria-label="Menu du profil"
                        className="flex items-center gap-2.5 hover:bg-slate-50 p-1.5 rounded-lg transition-colors"
                    >
                        <div className="hidden md:flex flex-col items-end min-w-0">
                            <span className="text-[12px] font-semibold text-slate-900 truncate max-w-[160px]">{displayName}</span>
                            <span className="text-[10px] text-slate-500">{user?.role === 'ADMIN' ? 'Administrateur' : 'Consultant'}</span>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-[#000091] text-white flex items-center justify-center text-[11px] font-black shadow-sm shadow-[#000091]/20">
                            {initials}
                        </div>
                        <ChevronDown className="w-3 h-3 text-slate-400" />
                    </button>

                    {menuOpen && (
                        <>
                            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                            <div className="absolute right-0 top-11 w-52 bg-white rounded-xl shadow-lg border-2 border-[#000091]/15 py-1 z-50">
                                <div className="px-3 py-2 border-b border-slate-100">
                                    <p className="text-[12px] font-bold text-slate-900 truncate">{displayName}</p>
                                    <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                                </div>
                                <button
                                    onClick={() => { setMenuOpen(false); router.push('/mon-compte?onglet=profil'); }}
                                    data-testid="topbar-mon-profil"
                                    className="w-full px-3 py-2 text-left text-[12px] text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    Mon profil
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="w-full px-3 py-2 text-left text-[12px] text-rose-700 hover:bg-rose-50 flex items-center gap-2 transition-colors"
                                >
                                    <LogOut className="w-3.5 h-3.5" />
                                    Déconnexion
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>

        {/* Sheet création rapide note de frais — monté ici pour être au-dessus de tout */}
        <ExpenseQuickCreateSheet open={quickCreateOpen} onClose={() => setQuickCreateOpen(false)} />
        {/* Sheet d'actions rapides générique (Accueil / CRA / Compte d'activité / Alertes) */}
        <QuickActionsSheet open={quickActionsOpen} onClose={() => setQuickActionsOpen(false)} />
        </>
    );
}
